import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import LocalEvent from 'utils/local-event';
import Log from 'utils/log';
import { getUserSettingsJson } from 'modules/settings/stores/user-settings-slice-handler';
import { getCompanySettings } from 'modules/settings/stores/company-settings-slice-handler';
import { DefaultPageMapping, MainMenuItemType } from 'constants/top-bar-constants';
import { EmergencyMessageSeverity, EmergencyCallProfileType, EmergencyStatus } from 'constants/emergencies';
import t from 'utils/translate';

const initialState = {
    currentSelected: null,
    counters: {},
    bellNotificationsOpen: false,
    emergenciesButtons: {
        emergencyCallStatus: EmergencyStatus.CLOSED,
        emergencyCallAllowed: false,
        emergencyMessageStatus: EmergencyStatus.CLOSED,
        emergencyMessageAllowed: false,
    },
};

class TopBarSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!TopBarSliceHandler.instance) {
            TopBarSliceHandler.instance = new TopBarSliceHandler();
        }

        return TopBarSliceHandler.instance;
    }

    constructor() {
        super('topBarSlice');

        LocalEvent.setListener(LocalEvent.eventTypes.LAYOUT_MANAGER_INITIAL_SCRIPTS_LOADED, () => {
            this.onInitialScriptsLoaded();
        });

        LocalEvent.setListener(LocalEvent.eventTypes.COMPANY_SETTINGS_LOADED, () => {
            this.onCompanySettingsUpdate();
        });

        LocalEvent.setListener(LocalEvent.eventTypes.COMPANY_SETTINGS_UPDATED, () => {
            this.onCompanySettingsUpdate();
        });

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                /**
                 * Select a top menu item
                 * @param {object} state
                 * @param {{payload: MenuItemOptions}} action
                 */
                setSelectedMenuItem(state, action) {
                    if (action.payload === state.currentSelected) {
                        return;
                    }

                    Log.debug(
                        'MAIN MENU',
                        `Selecting menu item '${action.payload}'. The currently selected item is: ${state.currentSelected}`,
                    );

                    state.currentSelected = action.payload;
                },

                setMenuItemCircleValue(state, action) {
                    action.payload.circleValue = parseInt(action.payload.circleValue, 10);

                    Log.debug(
                        'MAIN MENU',
                        `Set circle value "${action.payload.circleValue}" for "${action.payload.menuType}"`,
                    );

                    if (Number.isNaN(action.payload.circleValue) || action.payload.circleValue < 1) {
                        if (typeof state.counters[action.payload.menuType] !== 'undefined') {
                            delete state.counters[action.payload.menuType];
                        }
                        return;
                    }

                    if (action.payload.circleValue > 99) {
                        action.payload.circleValue = '99+';
                    }

                    state.counters = {
                        ...state.counters,
                        [action.payload.menuType]: action.payload.circleValue,
                    };
                },

                setEmergencyCallStatus(state, action) {
                    state.emergenciesButtons.emergencyCallStatus = action.payload;
                },

                setEmergencyMessageStatus(state, action) {
                    state.emergenciesButtons.emergencyMessageStatus = action.payload;
                },

                setEmergencyCallAllowed(state, action) {
                    state.emergenciesButtons.emergencyCallAllowed = action.payload;
                },

                setEmergencyMessageAllowed(state, action) {
                    state.emergenciesButtons.emergencyMessageAllowed = action.payload;
                },

                setBellNotificationsOpen(state, action) {
                    state.bellNotificationsOpen = action.payload;
                },
            },
        });
    }

    onCompanySettingsUpdate() {
        const companySettings = getCompanySettings();
        const { currentSelected } = this.getOwnState();

        if (
            (!companySettings.AllowWalkieTalkie && currentSelected === MainMenuItemType.CHANNELS) ||
            (!companySettings.IsDispatcher && currentSelected === MainMenuItemType.GEOLOCATION) ||
            (!companySettings.AllowMyBusiness && currentSelected === MainMenuItemType.MY_BUSINESS)
        ) {
            this.dispatch('setSelectedMenuItem', MainMenuItemType.CONVERSATIONS);
        }

        const emergencyCallAllowed =
            companySettings.AllowEmergencyCall &&
            companySettings.EmergencyCallStatus &&
            (companySettings.EmergencyCallProfile.Recipients.split(',').length > 0 ||
                companySettings.EmergencyCallProfile.EmergencyCallType ===
                    EmergencyCallProfileType.ACTIVE_SELECTED_CHANNEL);

        this.dispatch('setEmergencyCallAllowed', emergencyCallAllowed);

        const emergencyMessageAllowed =
            companySettings.AllowEmergencyMessage &&
            typeof companySettings.EmergencyMessageProfile !== 'undefined' &&
            companySettings.EmergencyMessageProfile.Recipients.split(',').length > 0;

        this.dispatch('setEmergencyMessageAllowed', emergencyMessageAllowed);
    }

    /**
     * Select a top-menu item
     * @param {MainMenuItemType} item
     */
    setSelectedMenuItem = (item) => {
        const currentlySelected = this.getOwnState().currentSelected;

        if (currentlySelected === item) {
            return;
        }

        this.dispatch('setSelectedMenuItem', item);

        LocalEvent.trigger(LocalEvent.eventTypes.MAIN_MENU_ITEM_SELECTED, {
            old: currentlySelected,
            current: item,
        });
    };

    getSelectedMenuItem = () => this.getOwnState().currentSelected;

    onInitialScriptsLoaded() {
        const userSettings = getUserSettingsJson();

        let defaultPage = MainMenuItemType.CONVERSATIONS;

        if (userSettings.defaultPage) {
            defaultPage =
                typeof DefaultPageMapping[userSettings.defaultPage] !== 'undefined'
                    ? DefaultPageMapping[userSettings.defaultPage]
                    : userSettings.defaultPage;
        }

        setTimeout(() => {
            this.setSelectedMenuItem(defaultPage);
        }, 0);
    }

    /**
     * Sets a circle with a counter over specific menu items
     * @param {MainMenuItemType} menuType
     * @param {number} circleValue
     */
    setMenuItemCircleValue = (menuType, circleValue) => {
        this.dispatch('setMenuItemCircleValue', { menuType, circleValue });
    };

    triggerEmergencyCall = () => {
        if (this.getOwnState().emergenciesButtons.emergencyCallStatus !== EmergencyStatus.CLOSED) {
            Log.warn('MAIN MENU', 'Another emergency call is in progress');
            return;
        }
        this.dispatch('setEmergencyCallStatus', EmergencyStatus.PENDING);
        LocalEvent.trigger(LocalEvent.eventTypes.EMERGENCY_CALL_TRIGGER, {});
    };

    setEmergencyCallStatus = (status) => {
        this.dispatch('setEmergencyCallStatus', status);
    };

    triggerEmergencyMessage = (severity) => {
        let message;
        const companySettings = getCompanySettings();
        switch (severity) {
            case EmergencyMessageSeverity.CRITICAL:
                message = `${t('js_all_emergency_alert_severity_critical_message_text_send')}`;
                break;
            case EmergencyMessageSeverity.SEVERE:
                message = `${t('js_all_emergency_alert_severity_severe_message_text_send')}`;
                break;
            case EmergencyMessageSeverity.URGENT:
                message = `${t('js_all_emergency_alert_severity_urgent_message_text_send')}`;
                break;
            default:
                severity = EmergencyMessageSeverity.CUSTOM;
                message = companySettings.EmergencyMessageProfile.CustomText;
        }
        Log.debug('MAIN MENU', `Trigger emergency message: ${message}`);
        LocalEvent.trigger(LocalEvent.eventTypes.EMERGENCY_MESSAGE_TRIGGER, {
            messageSeverity: severity,
        });
    };

    stopEmergencyMessage = () => {
        Log.debug('MAIN MENU', `Stop Emergency Message`);
        this.setEmergencyMessageStatus(EmergencyStatus.CLOSED);
        LocalEvent.trigger(LocalEvent.eventTypes.EMERGENCY_MESSAGE_STOP);
    };

    setEmergencyMessageStatus = (status) => {
        this.dispatch('setEmergencyMessageStatus', status);
    };

    setBellNotificationsOpen = (value) => {
        this.dispatch('setBellNotificationsOpen', value);
    };
}

export const topBarSliceHandler = TopBarSliceHandler.getInstance();
export const selectTopBar = (state) => state.topBarSlice;
export const selectEmergenciesButtons = (state) => state.topBarSlice.emergenciesButtons;
export const selectCircleCounters = (state) => state.topBarSlice.counters;
export const {
    setSelectedMenuItem,
    setMenuItemCircleValue,
    triggerEmergencyCall,
    setEmergencyCallStatus,
    triggerEmergencyMessage,
    stopEmergencyMessage,
    setBellNotificationsOpen,
} = topBarSliceHandler;
